import { useGetAppInitDetails } from 'hooks/use-get-app-init-details';
import { AppInitDetails } from 'api/get-app-init-details';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { useMatch } from 'react-router-dom';
import { captureMessage } from '@leagueplatform/observability';
import { SENTRY_CONSTANTS } from 'common/constants';

type UseMemberBrandingInfoReturn = {
  brandingInfo: {
    brandingLogo: AppInitDetails['brandingInfo']['brandingLogo'];
    brandingLogoOpt: AppInitDetails['brandingInfo']['brandingLogoOpt'];
    brandingMessage: AppInitDetails['brandingInfo']['brandingMessage'];
    brandingName: AppInitDetails['brandingInfo']['brandingName'];
  };
  shouldUseEnhancedMemberBranding: boolean;
};

/**
 * Returns the branding info for the member based on the enhanced member branding flag and the route.
 * If the member is not an enhanced member or if the route is not in the enhanced member branded routes, return the default branding info.
 * @returns {UseMemberBrandingInfoReturn} The branding info for the member.
 */
export const useMemberBrandingInfo = (): UseMemberBrandingInfoReturn => {
  const { brandingInfo, isEnhancedMember } = useGetAppInitDetails();
  const isEnhancedBrandedRoute = useMatch(APP_MANAGED_ROUTES.ENHANCED_BENEFITS);

  const defaultBrandingInfo = {
    brandingLogo: brandingInfo.brandingLogo,
    brandingLogoOpt: brandingInfo.brandingName,
    brandingMessage: brandingInfo.brandingMessage,
    brandingName: brandingInfo.brandingName,
  };
  // If the member is not an enhanced member or if the route is not in the enhanced member branded routes, return the default branding info
  if (!isEnhancedMember || !isEnhancedBrandedRoute)
    return {
      brandingInfo: defaultBrandingInfo,
      shouldUseEnhancedMemberBranding: false,
    };

  // Member is an enhanced member and the route is in the enhanced member branded routes
  if (
    brandingInfo?.enhancedMemberBrandingLogo ||
    brandingInfo?.enhancedMemberBrandingLogoOpt ||
    brandingInfo?.enhancedMemberBrandingName ||
    brandingInfo?.enhancedMemberBrandingMessage
  ) {
    return {
      brandingInfo: {
        brandingLogo: brandingInfo?.enhancedMemberBrandingLogo || '',
        brandingLogoOpt: brandingInfo?.enhancedMemberBrandingLogoOpt || '',
        brandingName: brandingInfo?.enhancedMemberBrandingName || '',
        brandingMessage: brandingInfo?.enhancedMemberBrandingMessage || '',
      },
      shouldUseEnhancedMemberBranding: true,
    };
  }

  // If the enhanced member branding info is not provided, return the default branding info
  captureMessage('Enhanced member branding info not provided.', {
    context: {
      Context: {
        info: 'Enhanced member branding info not found on app init details.',
      },
    },
    ownershipTeam: SENTRY_CONSTANTS.OWNERSHIP_TEAM,
    severityLevel: 'error',
  });

  return {
    brandingInfo: defaultBrandingInfo,
    shouldUseEnhancedMemberBranding: true,
  };
};
